/**
 * The Excel of "Last changes" (CU-869fn0gcp, criteria 16 and 22): the server's rows as arrays with a header, in the
 * page's unit, so the file has the screen's figures by construction. Two sheets: "Rates" (one row per ACRISS ×
 * pick-up date, from `cells`) and "Pushes" (the rows of the pushes table under the map). Nothing is recomputed
 * (TL C3): the unit is applied, that is all. The server only writes them. The simulated filter the rows were read
 * with travels in `file.simulated` (v1.5): the route reads nothing, so the file can say it. Pure and alias-free.
 */
import { diffInUnit, inUnit } from './amounts.js'

export const SHEETS = Object.freeze({ RATES: 'Rates', PUSHES: 'Pushes' })

const STATUS = {
	down: 'down', up: 'up', unchanged: 'no net change', no_decision: 'no decision', incomparable: 'not comparable',
}
const RESOLUTION = {
	sent: 'sent', capped: 'capped', already_set: 'already set', no_price_change: 'no price change',
	simulated: 'simulated', not_sent: 'not sent',
}
const isNum = v => typeof v === 'number' && Number.isFinite(v)
const r2 = v => (isNum(v) ? Math.round(v * 100) / 100 + 0 : null)
const pct = ratio => (isNum(ratio) ? Math.round(ratio * 1000) / 10 + 0 : null)
const pt = v => (isNum(v) ? Math.round(v * 10) / 10 + 0 : null)
const unitWord = unit => (unit === 'day' ? 'per day' : 'total')

/** `{ name, header, rows }` of the Rates sheet from the server's `cells`. */
export function ratesSheet(data, unit) {
	const lor = data?.sample?.lor || 1
	const u = unitWord(unit)
	const header = ['ACRISS', 'Pick-up', `Your price before (${u})`, `Your price after (${u})`, 'Your change %', `Your change € (${u})`,
		`Tier before (${u})`, `Tier after (${u})`, 'Tier change %', 'Δ vs market (pt)', 'Passes', 'Competitors (min)', 'Status', 'Simulated']
	const rows = (data?.cells || []).map(c => [
		c.sipp, c.pickup_date,
		r2(inUnit(c.own_start, unit, lor)), r2(inUnit(c.own_end, unit, lor)), pct(c.own_change), r2(diffInUnit(c.own_start, c.own_end, unit, lor)),
		r2(inUnit(c.market_start, unit, lor)), r2(inUnit(c.market_end, unit, lor)), pct(c.market_change), pt(c.delta_pt),
		c.n_passes ?? 0, c.n_comp_min ?? null, STATUS[c.status] || c.status || null, c.sim ? 'yes' : null,
	])
	return { name: SHEETS.RATES, header, rows }
}

/** `{ name, header, rows }` of the Pushes sheet from the pushes table's items (empty rows when nothing is selected). */
export function pushesSheet(items, unit, lor) {
	const u = unitWord(unit)
	const header = ['Pass', 'Time (UTC)', 'ACRISS', 'Pick-up', `Market before (${u})`, `Market after (${u})`, 'Market change %',
		`Your price before (${u})`, `Your price after (${u})`, `Observed next pass (${u})`, 'Your change %', 'Δ vs market (pt)', 'Resolution', 'Counted', 'Simulated', 'Strategy', 'Product']
	const rows = (items || []).map(p => [
		p.pass, p.ts, p.sipp, p.pickup_date,
		r2(inUnit(p.market_before, unit, lor)), r2(inUnit(p.market_after, unit, lor)), pct(p.market_change),
		r2(inUnit(p.own_before, unit, lor)), r2(inUnit(p.own_after, unit, lor)), r2(inUnit(p.own_observed_after, unit, lor)), pct(p.own_change), pt(p.delta_pt),
		RESOLUTION[p.resolution] || String(p.resolution || '').replace(/_/g, ' ') || null,
		p.counted ? 'yes' : 'no', p.sim ? 'yes' : null, p.strategy_name || null, p.product_name || null,
	])
	return { name: SHEETS.PUSHES, header, rows }
}

/** The body of `POST /api/repricing/last-changes/excel`: the file name parts and the two sheets. */
export function excelBody(data, view, pushes = []) {
	const lor = data?.sample?.lor || view.lor
	return {
		file: { office: view.office, lor, pickup_from: view.pickup_from, pickup_to: view.pickup_to, window: view.window,
			simulated: view.simulated === 'exclude' ? 'exclude' : 'include' },
		sheets: [ratesSheet(data, view.unit), pushesSheet(pushes, view.unit, lor)],
	}
}
